<template>
  <modal-template
    ref="modal"
    modal-size="sm"
    :title="$t('puzzle.editQuestionModal.title')"
    type-modal="puzzle_edit_question_modal"
  >
    <div class="modal-content__main">
      <d-form @submit="save()">
        <d-form-row class="form-group">
          <d-col cols="12">
            <label>{{ $t('puzzle.editQuestionModal.question') }}</label>
            <textarea
              v-model="question.question"
              class="form-control"
              :class="{ 'is-invalid': submitted && !question.question }"
            />
            <d-form-invalid-feedback>
              {{ $t('puzzle.editQuestionModal.questionInvalid') }}
            </d-form-invalid-feedback>
          </d-col>
        </d-form-row>

        <d-form-row class="form-group">
          <d-col cols="12">
            <label>{{ $t('puzzle.editQuestionModal.level') }}</label>
            <custom-select
              v-model="levelOption"
              :options="LEVEL_OPTIONS"
              trackBy="slug"
              :placeholder="$t('puzzle.editQuestionModal.levelPlaceholder')"
              @change="option => (question.level = option.slug)"
              :class="{ 'is-invalid': submitted && !question.level }"
            >
              <template #option="{ option }">
                <i :class="option.color" class="fas fa-circle mr-2" />
                {{ $t(option.labelKey) }}
              </template>
              <template #selected="{ option }">
                {{ $t(option.labelKey) }}
              </template>
            </custom-select>
            <d-form-invalid-feedback>
              {{ $t('puzzle.editQuestionModal.levelInvalid') }}
            </d-form-invalid-feedback>
          </d-col>
        </d-form-row>
      </d-form>
      <div class="small text-warning text-center">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <span class="text-dark">
          {{ $t('puzzle.editQuestionModal.warning') }}
        </span>
      </div>
    </div>

    <div slot="actions" class="modal-content__control">
      <d-button
        type="button"
        @click="save()"
        class="btn btn-sm btn-primary float-right"
      >
        {{ $t('shared.actions.save') }}
      </d-button>
      <d-button
        type="button"
        @click="close()"
        class="btn btn-sm btn-secondary mr-4 float-right"
      >
        {{ $t('shared.actions.cancel') }}
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import { mapActions } from 'vuex';
import Events from '_utils/events';
import ModalTemplate from '_shared/Modal.vue';
import PuzzleService from '_services/rest/modules/puzzle';
import CustomSelect from '_shared/CustomSelect.vue';
import { cloneDeep, LEVEL_OPTIONS, getLevelOption } from '_utils';
import formatErrors from '_common/mixins/FormatErrors';
import {
  _PUZZLE_EDIT_QUESTION_OPTION_,
  _PUZZLE_EDIT_QUESTION_UPDATED_,
} from '../PuzzleModel';
import puzzleReadOnly from '../../mixins/puzzleReadOnly';

export default {
  name: 'puzzle-edit-question-modal',
  mixins: [puzzleReadOnly, formatErrors],
  components: {
    ModalTemplate,
    CustomSelect,
  },
  data() {
    return {
      submitted: false,
      question: {
        id: null,
        question: null,
        level: null,
      },
      levelOption: null,
      LEVEL_OPTIONS,
    };
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    close() {
      this.setToggle({ type: 'puzzle_edit_question_modal', bool: false });
    },
    save() {
      if (!this.guardPuzzleMutation()) return;

      this.submitted = true;

      if (!this.validate()) {
        return;
      }

      PuzzleService.editQuestion(this.question)
        .then(() => {
          this.activeAlert({
            message: this.$t('puzzle.alert.questionUpdated'),
            theme: 'success',
          });
          Events.$emit(_PUZZLE_EDIT_QUESTION_UPDATED_, this.question);
          this.close();
        })
        .catch(error => {
          const message = this.formatErrors(error.data);
          this.activeAlert({ message, theme: 'danger' });
        })
        .finally(() => {
          this.submitted = false;
        });
    },
    validate() {
      if (!this.question.question || !this.question.level) {
        return false;
      }
      return true;
    },
  },
  destroyed() {
    Events.$off(_PUZZLE_EDIT_QUESTION_OPTION_);
  },
  mounted() {
    Events.$on(_PUZZLE_EDIT_QUESTION_OPTION_, question => {
      if (this.isReadOnly) return;

      this.question = cloneDeep(question);
      this.levelOption = getLevelOption(question.level);
    });
  },
};
</script>

<style lang="scss" scoped>
.modal {
  z-index: 8000;
  &-content__control {
    width: 100%;
  }
}
</style>
